import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Edit,
  Trash2,
  Plus,
  Calendar,
  CreditCard,
  Building,
  Maximize,
  Clock,
  MapPin,
} from 'lucide-react';
import { toast } from 'sonner';
import { parseISO, differenceInMonths } from 'date-fns';
import { supabase } from '@/lib/supabase';
import { usePropertyStore } from '@/stores/propertyStore';
import { useAuthStore } from '@/stores/authStore';
import { calculatePropertyMetrics } from '@/lib/calculations';
import { 
  formatCurrency, 
  formatPercent, 
  formatDate, 
  getInvestmentTypeLabel, 
  formatAddress, 
  getPropertyTypeLabel, 
  formatArea,
  getDismissalReasonLabel,
  cn
} from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { StatusBadge } from '@/components/ui/status-badge';
import { MetricCard } from '@/components/ui/metric-card';
import { Skeleton } from '@/components/ui/skeleton';
import { Card } from '@/components/ui/card';
import { ConfirmModal } from '@/components/ui/confirm-modal';
import { PropertyFormModal } from '@/components/properties/PropertyFormModal';
import { FinancingFormModal } from '@/components/properties/FinancingFormModal';
import { ImageGallery } from '@/components/properties/ImageGallery';
import { PropertySharing } from '@/components/properties/PropertySharing';
import { ActiveShareLinks } from '@/components/sharing/ActiveShareLinks';
import { DealCheckPanel } from '@/components/deal-check/DealCheckPanel';
import { DocumentList } from '@/components/documents/DocumentList';
import { PropertyTimeline } from '@/components/timeline/PropertyTimeline';
import { PropertyMap } from '@/components/properties/PropertyMap';
import type { Financing, Property } from '@/types';

function CostOverview({ property }: { property: Property }) {
  // Berechnungen
  const purchasePrice = property.purchase_price || 0;
  const additionalCosts =
    (property.cost_transfer_tax || 0) +
    (property.cost_notary || 0) +
    (property.cost_land_registry || 0) +
    (property.cost_broker || 0) +
    (property.cost_other || 0) ||
    (property.additional_costs || 0);

  const renovationCosts = property.renovation_cost_total || 0;

  // Haltekosten berechnen
  let holdingCosts = 0;
  if (property.holding_period_start && property.holding_cost_monthly) {
    const start = parseISO(property.holding_period_start);
    const end = property.holding_period_end ? parseISO(property.holding_period_end) : new Date();
    const months = Math.max(1, differenceInMonths(end, start));
    holdingCosts = property.holding_cost_monthly * months;
  }

  const totalInvestment = purchasePrice + additionalCosts + renovationCosts + holdingCosts;

  return (
    <Card className="p-5 border border-border bg-background-subtle">
      <h2 className="text-lg font-medium mb-4">Finanzielle Übersicht</h2>
      <div className="space-y-3">
        <div className="flex justify-between">
          <span className="text-foreground-muted">Kaufpreis</span>
          <span className="font-medium">{formatCurrency(purchasePrice)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-foreground-muted">Kaufnebenkosten</span>
          <span className="font-medium">{formatCurrency(additionalCosts)}</span>
        </div>
        {renovationCosts > 0 && (
          <div className="flex justify-between">
            <span className="text-foreground-muted">Sanierung</span>
            <span className="font-medium">{formatCurrency(renovationCosts)}</span>
          </div>
        )}
        {holdingCosts > 0 && (
          <div className="flex justify-between">
            <span className="text-foreground-muted">Haltekosten</span>
            <span className="font-medium text-negative">{formatCurrency(holdingCosts)}</span>
          </div>
        )}
        <div className="flex justify-between pt-3 border-t border-border mt-3">
          <span className="font-medium">Gesamt-Investition</span>
          <span className="font-semibold text-lg text-accent-gold">{formatCurrency(totalInvestment)}</span>
        </div>
        <div className="flex justify-between pt-3 border-t border-border">
          <dt className="text-foreground-muted">Marktwert</dt>
          <dd className="font-medium">{formatCurrency(property.current_value)}</dd>
        </div>
      </div>
    </Card>
  );
}

export default function PropertyDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentProperty, fetchProperty, deleteProperty, isLoading } = usePropertyStore();
  const { user } = useAuthStore();

  const [activeTab, setActiveTab] = useState('overview');
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [financingModalOpen, setFinancingModalOpen] = useState(false);
  const [financingDeleteModalOpen, setFinancingDeleteModalOpen] = useState(false);
  const [editingFinancing, setEditingFinancing] = useState<Financing | null>(null);
  const [deletingFinancing, setDeletingFinancing] = useState<Financing | null>(null);

  useEffect(() => {
    if (id) {
      fetchProperty(id);
    }
  }, [id, fetchProperty]);

  const handleDelete = async () => {
    if (id) {
      await deleteProperty(id);
      navigate('/properties');
    }
  };

  const handleFinancingEdit = (financing: Financing) => {
    setEditingFinancing(financing);
    setFinancingModalOpen(true);
  };

  const handleFinancingDelete = async () => {
    if (!deletingFinancing) return;

    try {
      const { error } = await supabase
        .from('financings')
        .delete()
        .eq('id', deletingFinancing.id);

      if (error) throw error;

      toast.success('Finanzierung gelöscht');
      if (id) fetchProperty(id);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Löschen';
      toast.error('Fehler beim Löschen', { description: message });
    } finally {
      setFinancingDeleteModalOpen(false);
      setDeletingFinancing(null);
    }
  };

  const handleFinancingSuccess = () => {
    if (id) fetchProperty(id);
  };

  if (isLoading || !currentProperty) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
      </div>
    );
  }

  const property = currentProperty;
  const metrics = calculatePropertyMetrics(property);

  const TABS = [
    { id: 'overview', label: 'Übersicht' },
    { id: 'deal-check', label: 'Deal-Check' },
    { id: 'timeline', label: 'Timeline' },
    { id: 'documents', label: 'Dokumente' },
    { id: 'media', label: 'Media' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Link
            to="/properties"
            className="p-2 -ml-2 rounded-md hover:bg-background-subtle"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-semibold">{property.name}</h1>
              <StatusBadge status={property.status} />
            </div>
            <p className="text-foreground-muted flex items-center gap-1 mt-1">
              <MapPin className="w-4 h-4" />
              {formatAddress(property)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="sm" onClick={() => setEditModalOpen(true)}>
            <Edit className="w-4 h-4 mr-2" />
            Bearbeiten
          </Button>
          <Button variant="secondary" size="sm" onClick={() => setDeleteModalOpen(true)}>
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-border overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={cn(
              'px-4 sm:px-6 py-3 text-sm font-medium transition-colors border-b-2 -mb-px whitespace-nowrap shrink-0',
              activeTab === tab.id
                ? 'border-foreground text-foreground'
                : 'border-transparent text-foreground-muted hover:text-foreground'
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="space-y-6 animate-fade-in">
          {/* Verwurf-Info */}
          {property.status === 'dismissed' && (
            <div className="p-4 rounded-lg bg-negative/5 border border-negative/10">
              <h3 className="text-negative font-medium flex items-center gap-2">
                Verworfen: {property.dismissal_reason ? getDismissalReasonLabel(property.dismissal_reason) : 'Kein Grund angegeben'}
              </h3>
              {property.dismissal_notes && (
                <p className="mt-2 text-sm text-foreground-muted">{property.dismissal_notes}</p>
              )}
            </div>
          )}

          {/* Kennzahlen */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <MetricCard
              label="Bruttomietrendite"
              value={formatPercent(metrics.bruttoRendite)}
            />
            <MetricCard
              label="Nettomietrendite"
              value={formatPercent(metrics.nettoRendite)}
            />
            <MetricCard
              label="EK-Rendite"
              value={formatPercent(metrics.eigenkapitalRendite)}
            />
            <MetricCard
              label="Cashflow / Monat"
              value={formatCurrency(metrics.cashflowMonatlich)}
              trend={metrics.cashflowMonatlich !== null
                ? metrics.cashflowMonatlich >= 0 ? 'positive' : 'negative'
                : undefined}
            />
          </div>

          <div className="grid gap-6 lg:grid-cols-3">
            {/* Basis-Infos */}
            <div className="lg:col-span-1 space-y-6">
              <div className="p-5 rounded-lg border border-border bg-background-subtle">
                <h2 className="text-lg font-medium mb-4">Objekt-Details</h2>
                <dl className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded bg-background-elevated flex items-center justify-center">
                      <Building className="w-4 h-4 text-foreground-muted" />
                    </div>
                    <div>
                      <dt className="text-xs text-foreground-muted uppercase tracking-wider">Typ</dt>
                      <dd className="font-medium">{getPropertyTypeLabel(property.property_type)}</dd>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded bg-background-elevated flex items-center justify-center">
                      <Maximize className="w-4 h-4 text-foreground-muted" />
                    </div>
                    <div>
                      <dt className="text-xs text-foreground-muted uppercase tracking-wider">Wohnfläche</dt>
                      <dd className="font-medium">{formatArea(property.living_area_sqm)}</dd>
                    </div>
                  </div>
                  {property.year_built && (
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded bg-background-elevated flex items-center justify-center">
                        <Clock className="w-4 h-4 text-foreground-muted" />
                      </div>
                      <div>
                        <dt className="text-xs text-foreground-muted uppercase tracking-wider">Baujahr</dt>
                        <dd className="font-medium">{property.year_built}</dd>
                      </div>
                    </div>
                  )}
                </dl>
              </div>

              <div className="p-5 rounded-lg border border-border bg-background-subtle">
                <h2 className="text-lg font-medium mb-4">Investment</h2>
                <dl className="space-y-3">
                  <div className="flex justify-between">
                    <dt className="text-foreground-muted">Strategie</dt>
                    <dd className="font-medium">{getInvestmentTypeLabel(property.investment_type)}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-foreground-muted">Einheiten</dt>
                    <dd className="font-medium">{property.num_units}</dd>
                  </div>
                  {property.floor !== null && (
                    <div className="flex justify-between">
                      <dt className="text-foreground-muted">Etage</dt>
                      <dd className="font-medium">{property.floor}</dd>
                    </div>
                  )}
                </dl>
              </div>

              {/* Karte */}
              <PropertyMap
                address={property.address_street || ''}
                city={property.address_city}
                zip={property.address_zip}
              />
            </div>

            {/* Finanzen & Miete */}
            <div className="lg:col-span-2 space-y-6">
              <div className="grid gap-6 sm:grid-cols-2">
                <CostOverview property={property} />

                <div className="p-5 rounded-lg border border-border bg-background-subtle">
                  <h2 className="text-lg font-medium mb-4">Mieteinnahmen</h2>
                  <dl className="space-y-3">
                    <div className="flex justify-between">
                      <dt className="text-foreground-muted">Kaltmiete / Monat</dt>
                      <dd className="font-medium text-positive">{formatCurrency(property.rent_cold)}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-foreground-muted">Warmmiete / Monat</dt>
                      <dd className="font-medium">{formatCurrency(property.rent_warm)}</dd>
                    </div>
                    <div className="flex justify-between border-t border-border pt-3 mt-3">
                      <dt className="text-foreground-muted">Jahresmiete (kalt)</dt>
                      <dd className="font-medium">
                        {formatCurrency(property.rent_cold ? property.rent_cold * 12 : null)}
                      </dd>
                    </div>
                  </dl>
                </div>
              </div>

              {/* Finanzierungen */}
              <div className="p-5 rounded-lg border border-border bg-background-subtle">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-medium">Finanzierungen</h2>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      setEditingFinancing(null);
                      setFinancingModalOpen(true);
                    }}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Hinzufügen
                  </Button>
                </div>

                {property.financings && property.financings.length > 0 ? (
                  <div className="space-y-3">
                    {property.financings.map((financing) => (
                      <div
                        key={financing.id}
                        className="flex items-center justify-between p-4 rounded-lg bg-background-elevated hover:bg-background transition-colors group border border-transparent hover:border-border"
                      >
                        <div
                          className="flex items-center gap-3 flex-1 cursor-pointer"
                          onClick={() => handleFinancingEdit(financing)}
                        >
                          <div className="w-10 h-10 rounded-full bg-background flex items-center justify-center">
                            <CreditCard className="w-5 h-5 text-accent-gold" />
                          </div>
                          <div>
                            <p className="font-medium text-foreground">
                              {formatCurrency(financing.loan_amount)}
                            </p>
                            <p className="text-xs text-foreground-muted">
                              {financing.interest_rate}% Zins • {financing.repayment_rate}% Tilgung
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right hidden sm:block">
                            <span className={cn(
                              "text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full",
                              financing.is_active ? "bg-positive/10 text-positive" : "bg-foreground-subtle/10 text-foreground-subtle"
                            )}>
                              {financing.is_active ? 'Aktiv' : 'Inaktiv'}
                            </span>
                            {financing.fixed_interest_until && (
                              <p className="text-[10px] text-foreground-subtle mt-1 uppercase tracking-tight">
                                Bis {formatDate(financing.fixed_interest_until)}
                              </p>
                            )}
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeletingFinancing(financing);
                              setFinancingDeleteModalOpen(true);
                            }}
                          >
                            <Trash2 className="w-4 h-4 text-negative" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-foreground-muted text-sm py-8 text-center bg-background/5 rounded-lg border border-dashed border-border">
                    Noch keine Finanzierungen hinterlegt.
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <PropertySharing
              propertyId={property.id}
              shares={property.shares || []}
              ownerId={property.owner_id}
              onSharesUpdate={() => fetchProperty(property.id)}
            />

            <div className="p-5 rounded-lg border border-border bg-background-subtle">
              <ActiveShareLinks
                propertyId={property.id}
                isOwner={property.owner_id === user?.id}
              />
            </div>

            {property.notes && (
              <div className="p-5 rounded-lg border border-border bg-background-subtle">
                <h2 className="text-lg font-medium mb-3">Interne Notizen</h2>
                <p className="text-foreground-muted text-sm whitespace-pre-wrap leading-relaxed">{property.notes}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'deal-check' && (
        <div className="animate-fade-in">
          <DealCheckPanel propertyId={property.id} />
        </div>
      )}

      {activeTab === 'timeline' && (
        <div className="animate-fade-in">
          <PropertyTimeline propertyId={property.id} />
        </div>
      )}

      {activeTab === 'documents' && (
        <div className="animate-fade-in">
          <DocumentList propertyId={property.id} />
        </div>
      )}

      {activeTab === 'media' && (
        <div className="p-5 rounded-lg border border-border bg-background-subtle animate-fade-in">
          <h2 className="text-lg font-medium mb-4">Bilder & Dokumente</h2>
          <ImageGallery
            propertyId={property.id}
            images={property.images || []}
            onRefresh={() => fetchProperty(property.id)}
          />
        </div>
      )}

      {/* Meta */}
      <div className="flex items-center gap-6 pt-6 border-t border-border text-[10px] uppercase tracking-widest font-medium text-foreground-subtle">
        <span className="flex items-center gap-2">
          <Calendar className="w-3 h-3" />
          Erstellt am {formatDate(property.created_at)}
        </span>
        <span className="flex items-center gap-2">
          <Clock className="w-3 h-3" />
          Zuletzt aktualisiert: {formatDate(property.updated_at)}
        </span>
      </div>

      {/* Modals */}
      <PropertyFormModal
        open={editModalOpen}
        onOpenChange={setEditModalOpen}
        property={property}
      />

      <ConfirmModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDelete}
        title="Immobilie löschen"
        description={`Möchtest du "${property.name}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`}
        isLoading={isLoading}
      />

      <FinancingFormModal
        isOpen={financingModalOpen}
        onClose={() => {
          setFinancingModalOpen(false);
          setEditingFinancing(null);
        }}
        propertyId={property.id}
        financing={editingFinancing}
        onSuccess={handleFinancingSuccess}
      />

      <ConfirmModal
        isOpen={financingDeleteModalOpen}
        onClose={() => {
          setFinancingDeleteModalOpen(false);
          setDeletingFinancing(null);
        }}
        onConfirm={handleFinancingDelete}
        title="Finanzierung löschen"
        description={deletingFinancing ? `Möchtest du die Finanzierung über ${formatCurrency(deletingFinancing.loan_amount)} wirklich löschen?` : ''}
        isLoading={false}
      />
    </div>
  );
}
